"use client";

import { useEffect, useMemo, useRef } from "react";

import { useNoteNotation } from "@/hooks/use-notation";
import { useReducedMotion } from "@/hooks/use-reduced-motion";
import type { Song } from "@/lib/music/song";
import { tonalityLabel } from "@/lib/music/tonality";
import {
  getPieceOverviewData,
  type PieceOverviewData,
} from "@/lib/piece-overview/piece-overview-data";
import { Collapsible } from "./collapsible";
import { DifficultyHeatmap } from "./difficulty/difficulty-heatmap";
import { PieceEyebrow } from "./eyebrow/piece-eyebrow";
import { FadeUpSection } from "./fade-up-section";
import { PieceProgressionInline } from "./harmony/piece-progression-inline";
import { PieceHero } from "./hero/piece-hero";
import { ARRIVE, AURORA, EASE_MOVE, EASE_OUT, FADE, SHIFT, ease, pick } from "./motion";
import { PieceProgram } from "./program/piece-program";
import { PieceStats } from "./stats/piece-stats";

/**
 * Ce que la fiche montre : le morceau, ou le choix du parcours.
 *
 * Deux états d'un même écran, et non deux écrans. C'est le sujet de tout ce
 * fichier.
 */
export type PieceOverviewPhase = "piece" | "programme";

interface PieceOverviewOverlayProps {
  song: Song;
  phase: PieceOverviewPhase;
  /** « Apprendre » : la fiche se retourne sur ses deux parcours. */
  onEnterProgram: () => void;
  /** Replier le choix. */
  onBackToPiece: () => void;
  onFreePlay: () => void;
  /**
   * `null` keeps the "Explorer →" link hidden, useful when the host
   * doesn't have a harmony route to surface.
   */
  onOpenHarmony: (() => void) | null;
  onSeekToMeasure: (measure: number) => void;
  /** L'échelle du parcours harmonique, ou `null` sans analyse. */
  rungIds: readonly string[] | null;
  /** Un niveau est en train de se poser sous les mains. */
  busy: boolean;
  onPickRung: (index: number) => void;
  onByHeart: () => void;
}

/**
 * "Souffle" presentation overlay shown after a piece is loaded. Sits
 * inside `StudioFrame.overlay` so the sidebar / minimap / piano keys
 * stay framed around it. Layout = three vertical bands :
 *
 *  - eyebrow row at the top (musical identity + at-a-glance numbers)
 *  - vertical-centered hero block (title + composer + stats + progression
 *    + inline action row "Apprendre  |  ou explorer librement")
 *  - difficulty horizon at the bottom (clickable bars)
 *
 * A soft aurora made of two radial gradients sits behind everything so
 * the page breathes without any cards.
 *
 * ## Elle porte aussi le choix des deux parcours
 *
 * Il a eu son écran, une vue du studio nommée `porch`, avec son fond, son
 * surtitre et sa titraille. Elle doublait cette page-ci : même titre, même
 * compositeur, même tonalité, et « Apprendre » était déjà ici. Deux écrans pour
 * une seule porte, dont le second effaçait le premier.
 *
 * C'est donc un état de la fiche. « Apprendre » ne mène plus nulle part : la
 * ligne d'action se déplie en deux colonnes, les quatre respirations et la
 * progression s'en vont, la titraille remonte et se resserre. Rien ne se monte,
 * rien ne se démonte, et c'est pour ça que ça se lit comme un seul geste. La
 * chorégraphie entière est dans `motion.ts`, la mécanique dans `Collapsible`.
 *
 * Keyboard : `Esc` replie le choix quand il est ouvert, et déclenche le jeu
 * libre sinon ; `Entrée` ouvre le choix depuis la fiche, et ne fait rien une
 * fois qu'il est ouvert. Deux gestes de même rang y attendent, et en désigner
 * un pour le joueur démentirait la symétrie que l'écran défend : c'est le focus
 * qui tranche, et il est posé sur le premier.
 */
export function PieceOverviewOverlay({
  song,
  phase,
  onEnterProgram,
  onBackToPiece,
  onFreePlay,
  onOpenHarmony,
  onSeekToMeasure,
  rungIds,
  busy,
  onPickRung,
  onByHeart,
}: PieceOverviewOverlayProps) {
  const data = useMemo<PieceOverviewData>(
    () => getPieceOverviewData(song),
    [song],
  );
  const notation = useNoteNotation();
  const reduced = useReducedMotion();
  const root = useRef<HTMLDivElement | null>(null);

  const program = phase === "programme";
  const shift = pick(SHIFT, program);

  // La tonalité s'épelle ici et pas chez l'appelant : c'est le réglage des
  // **notes** qui décide de « Sol mineur » contre « G mineur ».
  const key = data.tonality === null ? null : tonalityLabel(data.tonality, notation);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) {
        return;
      }

      const target = event.target as HTMLElement | null;
      const tag = target?.tagName?.toLowerCase();

      if (tag === "input" || tag === "textarea" || target?.isContentEditable) {
        return;
      }

      if (event.key === "Escape") {
        event.preventDefault();

        // Deux crans de sortie : le premier replie, le second ferme. Fermer
        // depuis le choix sauterait par-dessus la question qu'on vient de
        // poser.
        if (program) {
          onBackToPiece();
        } else {
          onFreePlay();
        }

        return;
      }

      if (event.key === "Enter" && !program) {
        event.preventDefault();
        onEnterProgram();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [program, onFreePlay, onEnterProgram, onBackToPiece]);

  /*
    Le focus suit l'écran, sinon le clavier le perd.

    Le geste qu'on vient d'actionner disparaît sous le doigt, donc le focus
    retombe sur `body` et la tabulation repart du début du document. Il se
    repose sur **le geste attendu** et non sur un conteneur, pour qu'Entrée le
    déclenche tout de suite.

    Tout de suite, et non à la fin de la chorégraphie : sept dixièmes de seconde
    sans focus sont sept dixièmes pendant lesquels une frappe se perd. Les blocs
    qui s'en vont sont `inert` dès le premier instant, donc rien de ce qu'on
    quitte n'est atteignable pendant ce temps-là.

    Pas au montage sur le morceau : la fiche s'ouvre toute seule au chargement,
    et arracher le focus à ce moment-là n'a été demandé par personne. Au montage
    sur le choix, si : on n'y arrive que par « Apprendre », depuis la barre du
    studio, et c'est le même geste que celui qui retourne la fiche déjà ouverte.
  */
  const settled = useRef<PieceOverviewPhase | null>(null);

  useEffect(() => {
    const previous = settled.current;
    settled.current = phase;

    if (previous === phase || (previous === null && phase === "piece")) {
      return;
    }

    const selector = phase === "programme" ? "[data-first-action]" : "[data-learn-action]";

    root.current?.querySelector<HTMLElement>(selector)?.focus({ preventScroll: true });
  }, [phase]);

  return (
    <div
      ref={root}
      style={{
        position: "relative",
        height: "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Aurora program={program} />

      {/*
        Ce que la page vient de devenir, pour qui ne la voit pas.

        Rien ne change de page, donc rien n'est annoncé de soi-même. Le message
        dit ce qui s'est passé, le focus dit où l'on est, et les deux ensemble
        remplacent la navigation qu'on a supprimée.
      */}
      <div aria-live="polite" className="sr-only">
        {program ? "Choix du parcours. Deux parcours sont proposés." : ""}
      </div>

      <div
        style={{
          position: "relative",
          zIndex: 1,
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <FadeUpSection delay={50}>
          <PieceEyebrow
            noteCount={data.noteCount}
            notesPerMeasure={data.notesPerMeasure}
            measures={data.measures}
            durationLabel={data.durationLabel}
            compact={program}
            onBack={onBackToPiece}
          />
        </FadeUpSection>

        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: `${program ? 26 : 32}px 40px 32px`,
            minHeight: 0,
            overflowY: "auto",
            transition: reduced ? "none" : ease("padding-top", shift, EASE_MOVE),
          }}
        >
          {/*
            L'air, en espaceurs plutôt qu'en `justify-content: center`.

            Deux raisons. La première est que le choix s'ancre en haut : sans la
            difficulté et sans les quatre respirations, une composition centrée
            ferait **descendre** le titre de trente pixels au lieu de le faire
            monter, et le mouvement dirait le contraire de ce qu'il fait. La
            seconde est qu'une boîte qui défile ne se centre pas : ce qui
            déborde par le haut devient alors inatteignable, alors qu'un
            espaceur ne prend que la place libre et disparaît quand il n'y en a
            plus.

            `flex-grow` est un nombre, donc il s'interpole, et il part dans le
            même intervalle et sur la même courbe que tout le reste.
          */}
          <Spacer grow={program ? 0 : 1} shift={shift} />

          <FadeUpSection delay={120}>
            <PieceHero
              title={data.title}
              composer={data.composer}
              context={data.context}
              tonality={key}
              compact={program}
            />
          </FadeUpSection>

          <FadeUpSection delay={190}>
            <Collapsible gone={program} shift={shift} fade={null} pad={32}>
              <PieceStats
                tonality={data.tonality}
                timeSignature={data.timeSignature}
                bpm={data.bpm}
                tessitura={data.tessitura}
                leaving={program}
              />
            </Collapsible>
          </FadeUpSection>

          <FadeUpSection delay={260}>
            <Collapsible
              gone={program}
              shift={shift}
              fade={pick(FADE.progression, program)}
              pad={32}
              lift={-8}
            >
              <PieceProgressionInline
                tonality={data.tonality}
                signatureProgression={data.signatureProgression}
                onOpenHarmony={onOpenHarmony}
              />
            </Collapsible>
          </FadeUpSection>

          <FadeUpSection delay={310}>
            <Collapsible
              gone={program}
              shift={shift}
              fade={pick(FADE.action, program)}
              pad={28}
            >
              <PieceActionRow onEnterProgram={onEnterProgram} onFreePlay={onFreePlay} />
            </Collapsible>
          </FadeUpSection>

          <Collapsible gone={!program} shift={shift} fade={null} pad={44} stretch>
            <PieceProgram
              shown={program}
              rungIds={rungIds}
              busy={busy}
              onPickRung={onPickRung}
              onByHeart={onByHeart}
            />
          </Collapsible>

          {/*
            Le pied se détache du bloc, sans partir flotter seul au fond de la
            pièce : cet espaceur-ci est borné, celui d'après ne l'est pas.
          */}
          <Spacer grow={1} max={72} shift={shift} />

          <Collapsible gone={!program} shift={shift} fade={null} pad={0}>
            <FreePlayInlineButton onClick={onFreePlay} shown={program} />
          </Collapsible>

          <Spacer grow={program ? 1 : 0} shift={shift} />
        </div>

        {data.difficulty !== null ? (
          <FadeUpSection delay={330}>
            <Collapsible
              gone={program}
              shift={shift}
              fade={pick(FADE.difficulty, program)}
              pad={0}
              // Le seul bloc situé sous le point de clic, donc le seul que la
              // vague pousse vers le bas. C'est cet écartement qui donne au
              // mouvement son amplitude.
              lift={22}
            >
              <DifficultyHeatmap
                measures={data.difficulty}
                onSelectMeasure={onSeekToMeasure}
              />
            </Collapsible>
          </FadeUpSection>
        ) : null}
      </div>
    </div>
  );
}

/**
 * L'aurore, en deux calques qui se croisent.
 *
 * Elle monte et s'ouvre quand le choix s'installe, parce que le contenu s'ancre
 * en haut et que le bas se vide : la lumière suit ce que la page fait. Et elle
 * finit après tout le reste, ce qui est la traîne du geste.
 *
 * Deux calques et un fondu croisé plutôt qu'un `background-position` animé :
 * une position de fond ne se composite pas, elle repeint, et une aurore de
 * huit cents pixels repeinte à chaque image sur sept dixièmes de seconde coûte
 * plus cher que toute la chorégraphie réunie.
 *
 * Le calque cyan ne bouge pas : il est décoratif et asymétrique, le déplacer
 * casserait l'équilibre.
 */
function Aurora({ program }: { readonly program: boolean }) {
  const reduced = useReducedMotion();
  const beat = pick(AURORA, program);

  const layer = (shown: boolean, violet: string): React.CSSProperties => ({
    position: "absolute",
    inset: 0,
    background: `${violet},
      radial-gradient(ellipse 600px 300px at 30% 30%, rgba(103,232,249,0.06) 0%, transparent 70%)`,
    opacity: shown ? 1 : 0,
    transition: reduced ? "none" : ease("opacity", beat, EASE_OUT),
    pointerEvents: "none",
    zIndex: 0,
  });

  return (
    <>
      <div
        aria-hidden
        style={layer(
          !program,
          "radial-gradient(ellipse 800px 400px at 50% 70%, rgba(139,92,246,0.15) 0%, transparent 60%)",
        )}
      />
      <div
        aria-hidden
        style={layer(
          program,
          "radial-gradient(ellipse 860px 460px at 50% 52%, rgba(139,92,246,0.18) 0%, transparent 60%)",
        )}
      />
    </>
  );
}

/**
 * L'air, qui se répartit et se referme.
 *
 * `flex-basis: 0` et `flex-shrink: 0` : il ne prend que ce qui reste, et jamais
 * de place à ce qu'il entoure.
 */
function Spacer({
  grow,
  max,
  shift,
}: {
  readonly grow: number;
  /** Une borne, pour l'air qui ne doit pas s'étirer indéfiniment. */
  readonly max?: number;
  readonly shift: { readonly delay: number; readonly ms: number };
}) {
  const reduced = useReducedMotion();

  return (
    <div
      aria-hidden
      style={{
        flexGrow: grow,
        flexShrink: 0,
        flexBasis: 0,
        maxHeight: max,
        transition: reduced ? "none" : ease("flex-grow", shift, EASE_MOVE),
      }}
    />
  );
}

interface PieceActionRowProps {
  onEnterProgram: () => void;
  onFreePlay: () => void;
}

/**
 * Inline action row sitting at the bottom of the hero column. Two
 * text-styled buttons separated by a hairline : violet "Apprendre →"
 * (primary, persistent underline) and dimmed "ou explorer librement"
 * (tertiary). Replaces the previous sticky footer's CTAs ; keyboard
 * shortcuts (Esc / Enter) remain wired in `PieceOverviewOverlay`.
 *
 * C'est aussi la figure que l'état de choix déplie : deux gestes centrés
 * séparés par un filet vertical sur l'axe. Voir `PieceProgram`, qui les écarte
 * et donne à chacun un nom et une phrase, sur la même verticale.
 */
function PieceActionRow({ onEnterProgram, onFreePlay }: PieceActionRowProps) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <LearnInlineButton onClick={onEnterProgram} />
      <span
        aria-hidden
        style={{
          display: "inline-block",
          width: 1,
          height: 12,
          background: "var(--color-border)",
          margin: "0 14px",
        }}
      />
      <FreePlayInlineButton onClick={onFreePlay} />
    </div>
  );
}

function LearnInlineButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-learn-action
      aria-label="Apprendre le morceau"
      style={{
        background: "transparent",
        border: "none",
        padding: "4px 6px",
        cursor: "pointer",
        color: "var(--color-accent-bright)",
        fontFamily: "Inter, sans-serif",
        fontWeight: 600,
        fontSize: 17,
        letterSpacing: "0.005em",
        display: "inline-flex",
        alignItems: "center",
      }}
      onMouseEnter={(e) => {
        const chevron = e.currentTarget.querySelector<HTMLSpanElement>(
          "[data-chevron]",
        );

        if (chevron) {
          chevron.style.transform = "translateX(2px)";
        }
      }}
      onMouseLeave={(e) => {
        const chevron = e.currentTarget.querySelector<HTMLSpanElement>(
          "[data-chevron]",
        );

        if (chevron) {
          chevron.style.transform = "translateX(0)";
        }
      }}
    >
      <span>Apprendre</span>
      <span
        aria-hidden
        data-chevron
        style={{
          display: "inline-block",
          marginLeft: 6,
          transition: "transform 180ms ease",
        }}
      >
        →
      </span>
    </button>
  );
}

/**
 * La sortie, qui traverse les deux états.
 *
 * Le même mot, la même encre, la même taille, au centre de la ligne d'action
 * puis au pied de la composition. Deux instances qui se croisent : un mot gris
 * qui s'éteint au milieu et se rallume en bas se lit comme un déplacement.
 *
 * Il reste petit et gris dans les deux, et c'est juste : ce n'est pas un
 * troisième choix de même rang que les deux parcours, c'est une sortie.
 */
function FreePlayInlineButton({
  onClick,
  shown,
}: {
  onClick: () => void;
  /**
   * Le pied s'allume avec un retard : c'est l'instance du bas, qui attend que
   * les deux colonnes soient là. Absent pour celle qui vit dans la ligne
   * d'action, dont la boîte tient déjà l'extinction.
   */
  shown?: boolean;
}) {
  const reduced = useReducedMotion();

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Jouer librement"
      style={{
        background: "transparent",
        border: "none",
        padding: "4px 6px",
        cursor: "pointer",
        color: "var(--color-text-secondary)",
        fontFamily: "Inter, sans-serif",
        fontWeight: 400,
        fontSize: 13,
        opacity: shown === false ? 0 : 1,
        transition:
          shown === undefined || reduced
            ? "color 150ms"
            : `color 150ms, opacity ${shown ? 300 : 160}ms ${shown ? ARRIVE.foot : 0}ms ${EASE_OUT}`,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.color = "var(--color-text-primary)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.color = "var(--color-text-secondary)";
      }}
    >
      ou explorer librement
    </button>
  );
}
